/**
 * The client for the frozen server. ADR-0004.
 *
 * Ported from `legacy/frontend/src/api.ts` at legacy commit 2953e3f: same base URL rule,
 * same timeout, same token handling, same error mapping, same forbidden handler. The one
 * difference is that the **types come from `@contracts`** rather than being re-typed
 * here (ADR-0011) — the old client kept its own copy of every server shape, in parallel
 * with `schemas.py`, with nothing checking that the two agreed. See FND-010 for what
 * that cost.
 *
 * Methods arrive with their flow. What is here covers F-000, F-001, F-004, F-005, F-006, F-007,
 * F-008a, F-009, F-011, F-014, F-016, F-017, F-018, F-019, F-020, F-021, F-023, F-024, F-025,
 * F-026 and F-030.
 */

import type {
  AcceptInviteRequest,
  ArrivalsFeed,
  AttendanceResponse,
  AttendanceStatus,
  AuthRequest,
  AuthResponse,
  Birthday,
  BirthdayToday,
  ChoirAttendanceMatrix,
  ChoirAttendanceSummary,
  ChoirSongRow,
  CreateChoirRequest,
  CreateMemberRequest,
  CurrentSession,
  DeleteMemberRequest,
  EditSessionRequest,
  ExcuseRequest,
  FailureBody,
  Function,
  InviteLink,
  InvitePreview,
  JoinRequest,
  JournalSent,
  LinkRequestToMemberRequest,
  ManualAttendanceRequest,
  MarkSermonRequest,
  MatrixMonths,
  MemberCard,
  MemberRow,
  MessageEnabledRequest,
  MyAttendance,
  NextService,
  PauseMemberRequest,
  PeerCodeResponse,
  Program,
  ProgramItemKind,
  Role,
  RsvpRequest,
  RsvpResponse,
  ScheduledMessageRequest,
  ScheduledMessageRow,
  ScheduledSession,
  ScheduleSessionRequest,
  SelfAttendanceRequest,
  SessionAttendance,
  SermonRota,
  SessionBrief,
  SessionCodeResponse,
  SessionHistoryPage,
  SessionKind,
  SetFunctionRequest,
  SetRoleRequest,
  SetRosterAdminRequest,
  UpdateMemberProfileRequest,
  VoicePart,
  WriteAccessRequest,
} from '@contracts/index';

// By default the same origin the app was loaded from: that covers both the deploy where
// the backend serves the Mini App itself and the tunnel used for testing on a phone. The
// old localhost default is dangerous here: on a phone that is the phone's own localhost,
// and the app silently loses its backend. A separate address goes in VITE_API_BASE_URL —
// needed only when Vite runs alongside the backend.
const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

/** See the `signal` on the fetch below for why a request may not take for ever. */
const REQUEST_TIMEOUT_MS = 15_000;

/**
 * The body of a refusal. `error` is one of the 56 codes in `@contracts/failures`;
 * `message` is what lands on the phone and is rendered verbatim, never paraphrased.
 *
 * The shape itself lives in `packages/contracts/src/failures.ts` and nowhere else
 * (ADR-0011); it is re-exported here so the modules that read it off this client go on
 * importing it from this module. FND-136.
 */
export type { FailureBody };

export class ApiError extends Error {
  readonly status: number;
  readonly failure: FailureBody | null;

  constructor(status: number, failure: FailureBody | null, message: string) {
    super(message);
    this.status = status;
    this.failure = failure;
  }
}

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

let onForbidden: (() => void) | null = null;

/**
 * Called when the server refuses a request because the person is not the regent.
 * `forbidden` has that one meaning and no other, so it is the signal a demotion gives:
 * the role travels in the launch response and is re-read on the server for every
 * request, and until this the client went on drawing the regent's buttons with every tap
 * coming back refused.
 */
export function setForbiddenHandler(handler: (() => void) | null): void {
  onForbidden = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (authToken !== null) headers.Authorization = `Bearer ${authToken}`;

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    // A phone that changed network, or a webview frozen with a request in the air,
    // leaves a fetch that never answers and never fails. Nothing above this line
    // survives that: a screen waits for ever, and a poll that skips a tick while the
    // previous call is in flight skips every tick after it. Fifteen seconds is far
    // longer than this API takes and far shorter than never.
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });

  if (!response.ok) {
    let failure: FailureBody | null = null;
    try {
      const payload = (await response.json()) as { detail?: FailureBody | string };
      if (payload.detail !== undefined && typeof payload.detail !== 'string') {
        failure = payload.detail;
      }
    } catch {
      failure = null;
    }
    if (failure?.error === 'forbidden') onForbidden?.();
    throw new ApiError(response.status, failure, failure?.message ?? `Ошибка ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Which page of the history is wanted. The old client has two methods for this —
 * `sessionHistory(before?)` (`legacy api.ts:956`) and `sessionHistoryById(sessionId)`
 * (`:962`) — and no call site ever passes both. The union keeps that call unwritable
 * rather than inventing a precedence the old app has no answer for.
 */
export type HistoryQuery =
  { before?: string; sessionId?: undefined } | { sessionId: string; before?: undefined };

// The old client's own shape, not the server's: the server takes the two halves as
// `avatar_eyes` and `avatar_color` (`UpdateMemberProfileRequest`), and this is how the card
// holds them between the two. `legacy api.ts:105-106`, comment and all. The server has allowed
// eight of each since 2026-09-04; the comment still says six, and is ported as it stands —
// FND-545, `KEEP`.
/** The two halves of an avatar a person picks for themselves, 1–6 each. */
export type Avatar = { eyes: number; color: number };

export const api = {
  authenticate: (initData: string) =>
    request<AuthResponse>('POST', '/auth/telegram', {
      init_data: initData,
    } satisfies AuthRequest),
  /** F-001. Five keys and never `timezone`; the server answers 201. `legacy api.ts:827-840`. */
  createChoir: (
    name: string,
    displayName: string,
    voicePart: VoicePart | null,
    birthday: Birthday | null,
    isBrother: boolean | null,
  ) =>
    request<AuthResponse>('POST', '/choir', {
      name,
      display_name: displayName,
      voice_part: voicePart,
      birthday,
      is_brother: isBrother,
    } satisfies CreateChoirRequest),
  /**
   * What an invite code opens. Null display_name is for the person who is
   * already in that choir: the server keeps the name they have, and joining is
   * then only a way of saying which choir should open.
   *
   * `legacy api.ts:841-847`.
   */
  invitePreview: (inviteCode: string) =>
    request<InvitePreview>('GET', `/choir/invite/${encodeURIComponent(inviteCode)}`),
  /** `legacy api.ts:848-869`. */
  joinChoir: (
    inviteCode: string,
    displayName: string | null,
    birthday: Birthday | null,
    isBrother: boolean | null,
    /**
     * Whether Telegram let the bot write to them, asked before this request
     * rather than after it. A link makes a request rather than a member since
     * 2026-08-27, so the answer has nowhere to go afterwards: there is no token
     * until the regent accepts, and `POST /auth/write-access` has nothing to
     * authenticate. Undefined leaves the server's own default, which is «no».
     */
    allowsWriteToPm?: boolean,
  ) =>
    request<AuthResponse>('POST', '/choir/join', {
      invite_code: inviteCode,
      display_name: displayName,
      // No part: the regent gives it, and until then the roster says so.
      birthday,
      is_brother: isBrother,
      allows_write_to_pm: allowsWriteToPm,
    } satisfies AcceptInviteRequest),
  /**
   * F-001 and F-030. `legacy api.ts:870-871`. The person's answer about messages in the private
   * chat, sent whichever way it went — after a create (F-001) and from «Я» (F-030). The server
   * answers **204** with no content, so nothing is read back (`request` above), and this endpoint
   * writes **no** `screen_views` row: `_note_view` is not called from
   * `legacy/backend/app/api/auth.py`, and the two rows that are ever written belong to
   * `GET /me/attendance` — FND-016.
   */
  setWriteAccess: (allowed: boolean) =>
    request<void>('POST', '/auth/write-access', { allowed } satisfies WriteAccessRequest),
  /**
   * F-003. `legacy api.ts:872`. The choir this app is showing, changed to another of the
   * person's own. The id is a path parameter, so there is no body and no query string, and
   * the answer is a whole new `AuthResponse` with a new token. Server-side the call is a
   * choice made explicitly: `remember_choice` writes `members.selected_at`
   * (`legacy/backend/app/api/auth.py:42-54`), which is what orders the list afterwards —
   * merely opening the app does not. Like `setWriteAccess` above it writes **no**
   * `screen_views` row: `_note_view` is not called from `legacy/backend/app/api/auth.py`
   * at all, and the product's usage series is written by the stats endpoints — FND-016.
   */
  switchChoir: (choirId: number) => request<AuthResponse>('POST', `/auth/choir/${choirId}`),
  me: () => request<AuthResponse>('GET', '/auth/me'),

  // F-004. `dueSession` is deliberately absent: the old client declared it and never
  // called it, so porting the declaration would port dead code. DEV-2, FND-018. The
  // endpoint stays in `packages/contracts/src/sessions.ts`, which describes the server
  // rather than what the client uses of it — and its path is written there and nowhere
  // here, which is what `api.test.ts` greps for.

  /** The regent's list: every gathering, past ones included. `legacy api.ts:932`. */
  schedule: () => request<ScheduledSession[]>('GET', '/session/schedule'),
  /** The singer's list: what is still ahead. `legacy api.ts:933`. */
  upcoming: () => request<ScheduledSession[]>('GET', '/session/upcoming'),
  /** `legacy api.ts:875`. Null when nothing is running. */
  currentSession: () => request<CurrentSession | null>('GET', '/session/current'),
  /**
   * A page of closed gatherings. `before` is the `effective_start` of the oldest row
   * already loaded (`ScheduleScreen.tsx:286`); `sessionId` fetches one closed gathering
   * off whatever page it would otherwise be on (`SessionsScreen.tsx:66`).
   */
  history: (query: HistoryQuery = {}) =>
    request<SessionHistoryPage>(
      'GET',
      query.sessionId !== undefined
        ? `/session/history?session_id=${encodeURIComponent(query.sessionId)}`
        : query.before === undefined
          ? '/session/history'
          : `/session/history?before=${encodeURIComponent(query.before)}`,
    ),
  /**
   * One kind per call: the two sets mean different things and are never mixed.
   * `legacy api.ts:972`.
   *
   * **This request writes a `screen_views` row** server-side
   * (`legacy/backend/app/api/stats.py:375`, via `_note_view` at `:268`) and is one of the
   * two endpoints that ever do. That record is the whole of the product's usage history,
   * so the new client must call this in the same situations the old one does or the series
   * breaks at cutover — `docs/release/CUTOVER.md` §3. The server would default `kind` to
   * `rehearsal`; the old client always states it, and so does this.
   */
  myAttendance: (kind: SessionKind) => request<MyAttendance>('GET', `/me/attendance?kind=${kind}`),
  /**
   * The choir's figures without names — the anonymous half of «Я», one kind per call.
   * `legacy api.ts:974-975`.
   *
   * This one writes **no** `screen_views` row (`legacy/backend/app/api/stats.py:443-469`),
   * unlike `myAttendance` above. It is **not role-gated on the server** either — any member
   * of the choir may ask; the gate is the old client's (`MeScreen.tsx:408-409`), and it
   * arrives with the screen. The server would default `kind` to `rehearsal`; the old client
   * always states it, and so does this.
   */
  choirSummary: (kind: SessionKind) =>
    request<ChoirAttendanceSummary>('GET', `/choir/attendance/summary?kind=${kind}`),
  /**
   * The choir's figures by name — «Таблица посещаемости», one kind per call.
   * `legacy api.ts:976-977`.
   *
   * **This request writes a `screen_views` row** server-side — `choir_attendance`
   * (`legacy/backend/app/api/stats.py:552`, via `_note_view` at `:268`) — and is the
   * other of the two endpoints that ever do (`FND-627`). The new client must call this in
   * the same situations the old one does or the series breaks at cutover —
   * `docs/release/CUTOVER.md` §3.
   */
  choirMatrix: (kind: SessionKind, months: MatrixMonths = 3) =>
    request<ChoirAttendanceMatrix>('GET', `/choir/attendance?kind=${kind}&months=${months}`),
  /**
   * The journal as a workbook, sent to the regent's own chat with the bot.
   *
   * Not a download: a Mini App runs inside Telegram's webview, where a download
   * is unreliable and on iOS frequently does nothing at all — with no error to
   * show for it. A document from the bot arrives everywhere and stays in the
   * chat, which is also where it is wanted.
   */
  sendJournal: (months: MatrixMonths) =>
    request<JournalSent>('POST', `/choir/journal?months=${months}`),
  /** `legacy api.ts:1091`. */
  birthdaysToday: () => request<BirthdayToday[]>('GET', '/choir/birthdays'),
  // F-019's three, `legacy api.ts:1008-1016`. None writes `screen_views` (`choirs.py:325-407`).
  inviteLink: () => request<InviteLink>('GET', '/choir/invite'),
  rotateInvite: () => request<InviteLink>('POST', '/choir/invite/rotate'),
  /**
   * The code the last rotation replaced, back on the choir. Refused a minute
   * after the rotation: rotating is how a leaked link is revoked, and an undo
   * with no expiry would let the leaked one return long after anybody was
   * looking at the bar offering it.
   */
  restoreInvite: () => request<InviteLink>('POST', '/choir/invite/restore'),
  // F-016's two reads. Neither writes a `screen_views` row (FND-016, FND-500).
  /** The whole roster, `pending` included, to every member — FND-504. `legacy api.ts:1017`. */
  choirMembers: () => request<MemberRow[]>('GET', '/choir/members'),

  // F-024's read and five writes. None writes a `screen_views` row (FND-016). `legacy api.ts:1043-1070`.
  /** The programme of one service. Any active member may read it and add to it. */
  program: (sessionId: string) => request<Program>('GET', `/session/${sessionId}/program`),
  addProgramItem: (
    sessionId: string,
    kind: ProgramItemKind,
    title: string,
    extra: { choirSongId?: number | null; singerIds?: number[] } = {},
  ) =>
    request<Program>('POST', `/session/${sessionId}/program`, {
      kind,
      title,
      choir_song_id: extra.choirSongId ?? null,
      singer_ids: extra.singerIds ?? [],
    }),
  /** The owner of the number, or a regent. */
  removeProgramItem: (itemId: number) => request<Program>('DELETE', `/program/${itemId}`),
  /** «Не спели», and the same call takes it back. */
  setProgramItemPerformed: (itemId: number, performed: boolean) =>
    request<Program>('POST', `/program/${itemId}/performed`, { performed }),
  /**
   * The whole order in one request, and never «move this one up»: two people
   * dragging the same list at once would each save half of it.
   */
  setProgramOrder: (sessionId: string, itemIds: number[]) =>
    request<Program>('POST', `/session/${sessionId}/program/order`, { item_ids: itemIds }),
  /** Who sings one number: the person who wrote it down, or the programme's keeper. */
  setProgramSingers: (itemId: number, memberIds: number[]) =>
    request<Program>('POST', `/program/${itemId}/singers`, { member_ids: memberIds }),

  // F-023's read and three writes. None writes a `screen_views` row (FND-016). `legacy api.ts:1071-1078`.
  /** What the choir has learned. Any member reads it; the regent writes it. */
  choirSongs: () => request<ChoirSongRow[]>('GET', '/choir/songs'),
  addChoirSong: (title: string, author: string | null) =>
    request<ChoirSongRow>('POST', '/choir/songs', { title, author }),
  /** A correction. Past programmes keep the name they were sung under. */
  updateChoirSong: (songId: number, title: string, author: string | null) =>
    request<ChoirSongRow>('PATCH', `/choir/songs/${songId}`, { title, author }),
  removeChoirSong: (songId: number) => request<void>('DELETE', `/choir/songs/${songId}`),
  // (Ours.) C-011, `FND-1604`: a day the choir sang a song, typed in by the regent, and taking a
  // typed day back. Both answer the song's row. `antifon api/songs.py:311-374`.
  addSungDate: (songId: number, sungOn: string) =>
    request<ChoirSongRow>('POST', `/choir/songs/${songId}/dates`, { sung_on: sungOn }),
  removeSungDate: (songId: number, dateId: number) =>
    request<ChoirSongRow>('DELETE', `/choir/songs/${songId}/dates/${dateId}`),
  // (Ours.) The comment below is ported verbatim and stale — FND-1537: «Песни хора» never calls
  // this, the hub's tile is its only caller. The legacy repository's to correct. `legacy api.ts:1079-1080`.
  /** Which service the tile on «Хор» and «Песни хора» both point at. */
  nextService: () => request<NextService | null>('GET', '/choir/next-service'),

  // F-026's read and four writes. None writes a `screen_views` row (FND-016). `legacy api.ts:1081-1089`.
  /** What the bot says to everybody, and when. The regent's alone. */
  choirMessages: () => request<ScheduledMessageRow[]>('GET', '/choir/messages'),
  addMessage: (draft: ScheduledMessageRequest) =>
    request<ScheduledMessageRow>('POST', '/choir/messages', draft),
  editMessage: (messageId: number, draft: ScheduledMessageRequest) =>
    request<ScheduledMessageRow>('POST', `/choir/messages/${messageId}`, draft),
  setMessageEnabled: (messageId: number, on: boolean) =>
    request<ScheduledMessageRow>('POST', `/choir/messages/${messageId}/enabled`, {
      on,
    } satisfies MessageEnabledRequest),
  removeMessage: (messageId: number) => request<void>('DELETE', `/choir/messages/${messageId}`),

  // F-017's nine writes on a person, in the old object's order. None of them writes a
  // `screen_views` row (`packages/contracts/src/members.ts`, the table of «The endpoints F-017
  // sends to»), and nothing here inspects a refusal: the status and the server's message reach
  // the card as they stand.

  /**
   * A member's name, part, avatar, birthday and sex, all six every time: the request
   * replaces the profile as a whole (`packages/contracts/src/members.ts`,
   * `UpdateMemberProfileRequest`).
   * `legacy api.ts:1018-1033`.
   */
  updateMemberProfile: (
    memberId: number,
    displayName: string,
    voicePart: VoicePart | null,
    avatar: Avatar,
    birthday: Birthday | null,
    isBrother: boolean | null,
  ) =>
    request<MemberRow>('POST', `/choir/members/${memberId}/profile`, {
      display_name: displayName,
      voice_part: voicePart,
      avatar_eyes: avatar.eyes,
      avatar_color: avatar.color,
      birthday,
      is_brother: isBrother,
    } satisfies UpdateMemberProfileRequest),
  /** `legacy api.ts:1092-1093`. */
  setMemberRole: (memberId: number, role: Role) =>
    request<MemberRow>('POST', `/choir/members/${memberId}/role`, {
      role,
    } satisfies SetRoleRequest),
  /**
   * Hand one of the two functions on, or take it back. The regent's, both ways
   * — and he keeps the function himself either way, which is why there is no
   * request here for «give it up».
   *
   * `legacy api.ts:1094-1100`, where `fn` is written `'sermons' | 'program'`; here it is the
   * contract's `Function`, the same two values.
   */
  setMemberFunction: (memberId: number, fn: Function, on: boolean) =>
    request<MemberRow>('POST', `/choir/members/${memberId}/role/${fn}`, {
      on,
    } satisfies SetFunctionRequest),
  /**
   * Out of the choir for a while, with the regent's own note or without one.
   * Reversible, and `resumeMember` is the reverse — the same screen, the same
   * shape, no new endpoint.
   *
   * `note` travels every time, `null` included. `legacy api.ts:1101-1107`.
   */
  pauseMember: (memberId: number, note: string | null) =>
    request<MemberRow>('POST', `/choir/members/${memberId}/pause`, {
      note,
    } satisfies PauseMemberRequest),
  /** No body, so no `Content-Type` either. `legacy api.ts:1108-1109`. */
  resumeMember: (memberId: number) =>
    request<MemberRow>('POST', `/choir/members/${memberId}/resume`),
  /**
   * Erase a person and every trace of them. The head regent's alone, and the
   * name has to be typed out character for character — trailing spaces are the
   * one liberty the server forgives.
   *
   * Answers with the choir's **new** invite link: the old one dies in the same
   * transaction, because it is the only way to shut the door on somebody the
   * app can no longer recognise.
   *
   * A JSON body on a `DELETE`, which `request` sends for any method. `legacy api.ts:1110-1120`.
   */
  deleteMember: (memberId: number, confirmName: string) =>
    request<InviteLink>('DELETE', `/choir/members/${memberId}`, {
      confirm_name: confirmName,
    } satisfies DeleteMemberRequest),

  /** Everything one roster row opens onto. Any member of the choir may ask. `legacy api.ts:1121-1123`. */
  memberCard: (memberId: number) => request<MemberCard>('GET', `/choir/members/${memberId}/card`),
  /** Who has followed the link and is waiting to be let in. Oldest first. */
  /**
   * Enter somebody who has no Telegram: a name, a part and a sex, no link and
   * no request. They are a member in every sense the choir is counted and in
   * none that needs a chat — see `has_telegram` on the row that comes back.
   *
   * All three keys every time, `null` included; the server answers 201. `legacy api.ts:1125-1139`.
   */
  createMember: (input: {
    displayName: string;
    voicePart: VoicePart | null;
    isBrother: boolean | null;
  }) =>
    request<MemberRow>('POST', '/choir/members', {
      display_name: input.displayName,
      voice_part: input.voicePart,
      is_brother: input.isBrother,
    } satisfies CreateMemberRequest),
  joinRequests: () => request<JoinRequest[]>('GET', '/choir/requests'),
  /**
   * The person at the door is already in the roster, under an account they
   * lost. The member keeps its id and its whole history; what moves onto it is
   * which Telegram it is and whether that Telegram lets the bot write. The
   * request row goes, so the roster's count does not change.
   *
   * `confirmName` where the chosen row still carries an account of its own,
   * because that account stops working the moment this returns — the same
   * confirmation `deleteMember` takes, and the server refuses without it.
   * A row with no account is linked on the tap alone, `null` and all.
   *
   * The key travels every time, `null` included. `legacy api.ts:1141-1155`.
   */
  linkRequest: (requestId: number, memberId: number, confirmName: string | null = null) =>
    request<MemberRow>('POST', `/choir/requests/${requestId}/link/${memberId}`, {
      confirm_name: confirmName,
    } satisfies LinkRequestToMemberRequest),
  /** No body, so no `Content-Type` either. `legacy api.ts:1156-1157`. */
  acceptRequest: (requestId: number) =>
    request<MemberRow>('POST', `/choir/requests/${requestId}/accept`),
  /** No body, and a 204 back, which `request` resolves `undefined` for. `legacy api.ts:1158-1159`. */
  declineRequest: (requestId: number) =>
    request<void>('POST', `/choir/requests/${requestId}/decline`),
  /**
   * Hand the seniority to another regent. The head regent's alone, and the one
   * way out of the rank — answers with the whole roster, because the mark moves
   * off one row and onto another in the same breath.
   *
   * No body. The card discards the roster it answers with (FND-540). `legacy api.ts:1160-1166`.
   */
  handOverSeniority: (memberId: number) =>
    request<MemberRow[]>('POST', `/choir/members/${memberId}/head`),
  /**
   * Let a second regent act on people, or stop letting him. The head's alone.
   * `legacy api.ts:1167-1169`.
   */
  setRosterAdmin: (memberId: number, on: boolean) =>
    request<MemberRow>('POST', `/choir/members/${memberId}/roster-admin`, {
      on,
    } satisfies SetRosterAdminRequest),

  // F-025, as C-002 (`FND-1591`, 2026-10-06) rebuilt it: the list of three, the keeper's two
  // writes on it, the brother's «не смогу» and the mark. Each answers the whole `SermonRota`
  // (`legacy/backend/app/api/sermons.py:81-248` at `fc74405`). `GET /sermons` writes **no**
  // `screen_views` row (FND-935): the only `_note_view` calls are in `api/stats.py`.
  sermonRota: () => request<SermonRota>('GET', '/sermons'),
  /** Put a brother on the list by hand; also takes back his «не смогу» for this week. */
  pinSermon: (memberId: number) => request<SermonRota>('POST', `/sermons/list/${memberId}`),
  /** Off the list until Monday; his place in the queue is kept. */
  unpinSermon: (memberId: number) => request<SermonRota>('DELETE', `/sermons/list/${memberId}`),

  /**
   * «Не смогу» — step off this week's list. No body and no date: it is about the week, and the
   * caller is the token's (`legacy/backend/app/api/sermons.py:195-207`).
   */
  declineSermon: () => request<SermonRota>('POST', '/sermons/decline'),
  /** Who preached: a brother, or null for «не было». */
  markSermon: (sessionId: string, memberId: number | null) =>
    request<SermonRota>('POST', `/sermons/${sessionId}/mark`, {
      member_id: memberId,
    } satisfies MarkSermonRequest),

  // F-005. The regent's three write doors, with the old client's bodies: all three fields
  // on every call, `title` as an explicit `null` when it is emptied, because the form
  // replaces the gathering's plan as a whole rather than patching what changed
  // (`packages/contracts/src/sessions.ts:229-234`). Nothing here inspects a refusal: the
  // status and the server's message reach the screen as they stand.

  /**
   * Book a gathering. `legacy api.ts:908-917`. The client reads only `session_id` off the
   * answer, to find the fresh row in the list it then reloads.
   *
   * `title` carries the server's `max_length=200` and **nothing on the client enforces
   * it** — FND-069, `KEEP` — so a longer one goes on the wire and comes back a Pydantic
   * 422, which has no `message` and lands on the screen as the bare `Ошибка 422` through
   * the fallback above. `kind` the server would default to `rehearsal`; the old client
   * always states it, and so does this.
   */
  scheduleSession: (input: { title: string | null; scheduledStart: string; kind: SessionKind }) =>
    request<SessionBrief>('POST', '/session/schedule', {
      title: input.title,
      scheduled_start: input.scheduledStart,
      kind: input.kind,
    } satisfies ScheduleSessionRequest),
  /**
   * Move a gathering, rename it, or change what it is — in place, so that every answer
   * keeps pointing at the same row. `legacy api.ts:927-929`.
   *
   * `kind` is **required** here, unlike the booking above: an edit that omitted it would
   * silently turn a service into a rehearsal (`packages/contracts/src/sessions.ts:236-239`).
   * `SessionBrief` comes back; the old client discards it (`SessionScreen.tsx:658`).
   */
  editScheduledSession: (
    sessionId: string,
    input: { title: string | null; scheduledStart: string; kind: SessionKind },
  ) =>
    request<SessionBrief>('POST', `/session/${sessionId}/edit`, {
      title: input.title,
      scheduled_start: input.scheduledStart,
      kind: input.kind,
    } satisfies EditSessionRequest),
  /**
   * Drop a gathering nobody opened. `legacy api.ts:938`. No body — the path carries the
   * id — and none back: the server answers 204, which `request` resolves `undefined` for.
   */
  removeScheduledSession: (sessionId: string) => request<void>('DELETE', `/session/${sessionId}`),

  // F-011. Answering a gathering in advance: `legacy api.ts:934-935`, placed before F-006's
  // group because legacy `:934` precedes `:936`. `POST …/rsvp` writes no `screen_views` row
  // (`backend/app/api/stats.py:375`, `:552` are the only writers).

  /**
   * Answer a gathering in advance, going or not going with a reason. `note` defaults to
   * `null` and is sent as `null`, so the body always carries both keys. `notePublic` is
   * «Причину увидят все» (C-018, `FND-1613`), sent every time like the note.
   */
  answerSession: (
    sessionId: string,
    response: RsvpResponse,
    note: string | null = null,
    notePublic = false,
  ) =>
    request<ScheduledSession>('POST', `/session/${sessionId}/rsvp`, {
      response,
      note,
      note_public: notePublic,
    } satisfies RsvpRequest),

  // F-006. `openSession` is deliberately absent: the old client declared it — the id-less
  // door that opens «whichever one is due» — and never called it, so porting the
  // declaration would port dead code. FND-100, `DROP`, following FND-018 above. The
  // endpoint stays in `packages/contracts/src/sessions.ts`, and its path is written there
  // and nowhere here, which is what `api.test.ts` greps for.

  /**
   * Open a gathering off the schedule, early. `legacy api.ts:936-937`. No body — the path
   * carries the id, interpolated raw as `removeScheduledSession` does above.
   */
  openScheduledSession: (sessionId: string) =>
    request<SessionBrief>('POST', `/session/${sessionId}/open`),

  /** Who is already at the running rehearsal. Any member; same rows_version contract as the code. `legacy api.ts:876-883`. */
  currentArrivals: (rowsVersion?: string) =>
    request<ArrivalsFeed>(
      'GET',
      rowsVersion === undefined
        ? '/session/current/arrivals'
        : `/session/current/arrivals?rows_version=${encodeURIComponent(rowsVersion)}`,
    ),
  /** Only for a member already marked at the running rehearsal; refused otherwise. `legacy api.ts:890-891`. */
  peerCode: () => request<PeerCodeResponse>('GET', '/session/current/peer-code'),
  /** The regent's plate, polled every two seconds. `legacy api.ts:892-898`. */
  currentCode: (rowsVersion?: string) =>
    request<SessionCodeResponse>(
      'GET',
      rowsVersion === undefined
        ? '/session/current/code'
        : `/session/current/code?rows_version=${encodeURIComponent(rowsVersion)}`,
    ),
  /** `legacy api.ts:905`. */
  closeSession: () => request<SessionBrief>('POST', '/session/close'),
  /** Undo the opening. Not closing: this one says the rehearsal did not happen. `legacy api.ts:906-907`. */
  cancelSession: () => request<void>('POST', '/session/cancel'),
  /**
   * Check in with the token a scanned QR carried in. `legacy api.ts:969`. The body carries
   * `token` and nothing else (`FND-161`), the mirror of `markByCode` below.
   */
  markByToken: (token: string) =>
    request<AttendanceResponse>('POST', '/attendance/self', {
      token,
    } satisfies SelfAttendanceRequest),
  /**
   * Check in with the six typed digits. `legacy api.ts:970`. The body carries `code` and
   * nothing else: the request shape has a `token` too
   * (`packages/contracts/src/attendance.ts:41-44`), and the absent key is what tells the
   * server which door was used.
   */
  markByCode: (code: string) =>
    request<AttendanceResponse>('POST', '/attendance/self', {
      code,
    } satisfies SelfAttendanceRequest),

  // F-014. The past roster: `legacy api.ts:939-955`, `:967-968`, `:988-1007`, in the old
  // object's order, with its doc comments. None of the five writes a `screen_views` row —
  // the only writers are `legacy/backend/app/api/stats.py:375` and `:552`. The old client
  // types the last two `request<MarkResult>`; this tree names that server shape
  // `AttendanceResponse`, as `markByCode` does above.

  /**
   * Strike a closed gathering and every mark on it, inside the edit window.
   *
   * The third of the three doors and the only one that undoes something
   * already recorded: `removeScheduledSession` drops one nobody opened and
   * `cancelSession` undoes an opening while nobody has come. Both of those say
   * «nothing happened»; this says «something happened and we are striking it».
   */
  eraseSession: (sessionId: string) => request<void>('POST', `/session/${sessionId}/erase`),
  /**
   * The regent has checked what the machine marked. It is what releases the
   * morning nudge: until it is said, the bot writes to nobody about absences
   * nobody has looked at. Refused on a gathering the regent closed himself —
   * that one never carries the flag and never needed the step.
   */
  confirmRoster: (sessionId: string) =>
    request<SessionAttendance>('POST', `/session/${sessionId}/reviewed`),
  sessionAttendance: (sessionId: string) =>
    request<SessionAttendance>('GET', `/session/${sessionId}/attendance`),
  markManually: (input: {
    sessionId: string;
    memberId: number;
    status: AttendanceStatus;
    /** The actual arrival time, ISO. Without it lateness stays unknown. */
    markedAt?: string | null;
  }) =>
    request<AttendanceResponse>('POST', '/attendance/manual', {
      session_id: input.sessionId,
      member_id: input.memberId,
      status: input.status,
      marked_at: input.markedAt ?? null,
    } satisfies ManualAttendanceRequest),
  setExcused: (sessionId: string, memberId: number, isExcused: boolean, note?: string | null) =>
    request<AttendanceResponse>('POST', '/attendance/excuse', {
      session_id: sessionId,
      member_id: memberId,
      is_excused: isExcused,
      note: note ?? null,
    } satisfies ExcuseRequest),
};
